import { z } from 'zod';
import { getBrowserTimezone } from './timezones';
import { addDays, setHours, setMinutes, setSeconds, setMilliseconds } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

// TCPA compliance hours (8 AM to 8 PM local time)
const TCPA_START_HOUR = 8;
const TCPA_END_HOUR = 20;

export class TCPACompliance {
  /**
   * Check if it's within TCPA calling hours
   */
  static isWithinCallHours(referenceTime: Date = new Date()): boolean {
    try {
      // Get browser's timezone
      const timezone = getBrowserTimezone();

      // Convert UTC time to local timezone
      const localTime = utcToZonedTime(referenceTime, timezone);
      const hour = localTime.getHours();
      
      // Check if within TCPA hours (8 AM to 8 PM local time)
      return hour >= TCPA_START_HOUR && hour < TCPA_END_HOUR;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get the next available calling time
   */
  static getNextAvailableTime(referenceTime: Date = new Date()): Date | null {
    try {
      const timezone = getBrowserTimezone();
      
      // Convert input UTC time to local timezone
      const localTime = utcToZonedTime(referenceTime, timezone);
      const hour = localTime.getHours();
      
      let nextAvailableLocal: Date;

      if (hour < TCPA_START_HOUR) {
        // If before start hours, set to start time today
        nextAvailableLocal = setMilliseconds(
          setSeconds(
            setMinutes(
              setHours(localTime, TCPA_START_HOUR),
              0
            ),
            0
          ),
          0
        );
      } else if (hour >= TCPA_END_HOUR) {
        // If after end hours, set to start time tomorrow
        nextAvailableLocal = setMilliseconds(
          setSeconds(
            setMinutes(
              setHours(
                addDays(localTime, 1),
                TCPA_START_HOUR
              ),
              0
            ),
            0
          ),
          0
        );
      } else {
        // If within hours, use current time
        nextAvailableLocal = localTime;
      }

      // Convert local time back to UTC
      return zonedTimeToUtc(nextAvailableLocal, timezone);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get all valid calling windows
   */
  static getCallWindows(date: Date = new Date()): Array<{ start: Date; end: Date }> {
    try {
      const timezone = getBrowserTimezone();
      
      // Convert to local timezone
      const localDate = utcToZonedTime(date, timezone);
      
      // Set to start of day
      const dayStart = setMilliseconds(
        setSeconds(
          setMinutes(
            setHours(localDate, 0),
            0
          ),
          0
        ),
        0
      );

      // Create window for the day
      const windowStart = setHours(dayStart, TCPA_START_HOUR);
      const windowEnd = setHours(dayStart, TCPA_END_HOUR);

      // Convert times back to UTC
      return [{
        start: zonedTimeToUtc(windowStart, timezone),
        end: zonedTimeToUtc(windowEnd, timezone)
      }];
    } catch (error) {
      return [];
    }
  }
}